// Turns the server-rendered "arriving in 2–3 weeks" from snippets/delivery-estimate
// into real dates for today. The figures come from the shop metafield
// `custom.delivery_estimates`, printed into the page by layout/theme.liquid.
//
// Country: the Markets country (localization.country) is used when the shopper
// picked one. Otherwise we ask Shopify's IP lookup, because countries outside a
// Market (most of the International zone) would otherwise show the primary
// market's dates.

if (!customElements.get('delivery-estimate')) {
  const DAY_MS = 86400000;
  const DETECTED_KEY = 'deliveryEstimateCountry';
  let detectedCountry;

  const readData = () => {
    const el = document.getElementById('DeliveryEstimates');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  };

  const detectCountry = () => {
    if (/(^|;\s*)localization=/.test(document.cookie)) return Promise.resolve(null);
    if (detectedCountry) return detectedCountry;

    try {
      const cached = sessionStorage.getItem(DETECTED_KEY);
      if (cached) return (detectedCountry = Promise.resolve(cached));
    } catch (e) {}

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    detectedCountry = fetch(`${root}browsing_context_suggestions.json?country[enabled]=true`)
      .then((response) => response.json())
      .then((json) => {
        const code = json && json.detected_values && json.detected_values.country && json.detected_values.country.handle;
        if (code) {
          try {
            sessionStorage.setItem(DETECTED_KEY, code);
          } catch (e) {}
        }
        return code || null;
      })
      .catch(() => null);
    return detectedCountry;
  };

  // Plain calendar days, the same arithmetic as Tapstitch's order screen.
  const arrivalDate = (days) => new Date(Date.now() + days * DAY_MS);

  const formatter = (country, options) => {
    const lang = document.documentElement.lang || 'en';
    try {
      return new Intl.DateTimeFormat(`${lang}-${country}`, options);
    } catch (e) {
      return new Intl.DateTimeFormat(lang, options);
    }
  };

  const countryName = (country) => {
    try {
      return new Intl.DisplayNames([document.documentElement.lang || 'en'], { type: 'region' }).of(country);
    } catch (e) {
      return country;
    }
  };

  class DeliveryEstimate extends HTMLElement {
    async connectedCallback() {
      const data = readData();
      const strings = window.deliveryEstimateStrings;
      if (!data || !data.countries || !strings) return;

      let country = this.dataset.country;
      const detected = await detectCountry();
      if (detected && data.countries[detected]) country = detected;

      const known = Boolean(data.countries[country]);
      let estimate = data.countries[country] || data.default;
      if (!estimate || !estimate.free) return;

      const [low, high] = data.percentiles || [60, 95];
      const [productionLow, productionHigh] = data.production || [0, 0];
      const doorToDoor = (transit) => transit && [transit[0] + productionLow, transit[1] + productionHigh];
      estimate = { free: doorToDoor(estimate.free), express: doorToDoor(estimate.express) };
      if (this.dataset.format === 'detail') {
        this.replaceChildren(...this.detail(estimate, country, known, low, high, strings));
      } else {
        this.textContent = this.short(estimate, country, strings);
      }
    }

    short(estimate, country, strings) {
      const range = formatter(country, { day: 'numeric', month: 'short' }).formatRange(
        arrivalDate(estimate.free[0]),
        arrivalDate(estimate.free[1])
      );
      return strings.short.replace('[range]', range);
    }

    detail(estimate, country, known, low, high, strings) {
      // '4 Nov', not 'Wednesday, 4 November': it is read at a glance.
      const long = formatter(country, { day: 'numeric', month: 'short' });
      const fill = (template, [early, late]) =>
        template
          .replace('[low]', low)
          .replace('[high]', high)
          .replace('[early]', long.format(arrivalDate(early)))
          .replace('[late]', long.format(arrivalDate(late)));

      // One line per option, free first, each led by its name in bold.
      const line = (label, text) => {
        const name = document.createElement('strong');
        name.textContent = label;
        return [name, ': ' + text];
      };
      const nodes = line(strings.freeLabel || 'Free', fill(strings.detail, estimate.free));
      if (estimate.express) {
        nodes.push(document.createElement('br'), ...line(strings.expressLabel || 'Express', fill(strings.express, estimate.express)));
      }
      if (known) nodes.push(document.createElement('br'), strings.country.replace('[country]', countryName(country)));
      return nodes;
    }
  }

  customElements.define('delivery-estimate', DeliveryEstimate);
}
